import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { NPSLoaderComponent, Nps } from '@app/@shared/nps/nps-loader.component';
import {
  AuthenticationService,
  BaseComponent,
  CredentialsService,
} from '@app/core';
import { ThemingService } from '@app/core/helperclasses/theming.service';
import { CourseCustomer } from '@app/models';
import { debounceTime, filter } from 'rxjs';
import { ShellService } from './shell.service';
import { environment } from '@env/environment';
import { ForeignTypes, WebsocketService } from '@app/core/websocket.service';
import { RoutingService } from '@app/core/routing.service';

@Component({
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
})
export class ShellComponent extends BaseComponent implements OnInit {
  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.updateDrawerMode((event.target as Window).innerWidth);
  }

  @ViewChild('nav', { static: true }) navElement!: ElementRef;
  public navVerticalOverflow = false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('vcNPS') vcNPS!: NPSLoaderComponent;
  public currentTheme: string = '';
  public customer: CourseCustomer | undefined;
  public drawerMode: 'over' | 'side' = 'over';

  private pageScrollElement: Element | undefined;
  private mainScrollElement: Element | undefined;

  public showScrollToTopButton = false;

  public baseUrl = environment.serverUrl + environment.apiVersion;

  @ViewChild('drawer') drawer!: MatDrawer;

  toggleDrawer() {
    this.drawer.toggle();
  }

  constructor(
    public shellService: ShellService,
    private authenticationService: AuthenticationService,
    private credentialService: CredentialsService,
    public themingService: ThemingService,
    private router: Router,
    private webSocket: WebsocketService,
    private routerService: RoutingService,
  ) {
    super();
    this.themingService.theme.subscribe((theme) => {
      this.currentTheme = theme;
    });
    this.shellService.loadNavigation();
  }

  ngOnInit() {
    this.addSubscription(
      this.webSocket.$websocketMessage.pipe(
        filter((resp) => resp?.foreign_type == ForeignTypes.COURSE_NAVIGATION),
        debounceTime(200),
      ),
    ).subscribe((res) => {
      if (res) {
        this.shellService.loadNavigation();
      }
    });

    this.addSubscription(
      this.router.events.pipe(
        filter((event) => event instanceof NavigationStart),
      ),
    ).subscribe(() => {
      this.startFadeInAnimation();
      setTimeout(() => {
        // this.pageScrollElement =
        //   document.querySelector('.page-modules') ?? undefined;
        // this.mainScrollElement =
        //   document.querySelector('#mainScrollElement') ?? undefined;
        if (this.mainScrollElement) {
          this.mainScrollElement.scrollTop = 0;
        }
        if (this.pageScrollElement) {
          this.pageScrollElement.scrollTop = 0;
        }
      }, 500);
    });
    this.addSubscription(
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
      ),
    ).subscribe(() => {
      this.endFadeInAnimation();
    });

    this.customer = this.credentialService.credentials?.customer;
    this.addSubscription(this.shellService.loadNPSInfo()).subscribe((res) => {
      this.loadNPS(res!.data);
    });

    this.updateDrawerMode(window.innerWidth);
  }

  ngAfterViewInit(): void {
    this.pageScrollElement =
      document.querySelector('.page-modules') ?? undefined;
    this.mainScrollElement =
      document.querySelector('#mainScrollElement') ?? undefined;

    if (this.mainScrollElement) {
      this.mainScrollElement.scrollTop = 0;
      this.mainScrollElement.addEventListener(
        'scroll',
        this.onScroll.bind(this),
      );
    }
    if (this.pageScrollElement) {
      this.pageScrollElement.scrollTop = 0;
      this.pageScrollElement.addEventListener(
        'scroll',
        this.onScroll.bind(this),
      );
    }

    setTimeout(() => {
      this.checkNavOverflow();
    }, 100);

    window.addEventListener('resize', () => this.checkNavOverflow());
  }

  checkNavOverflow(): void {
    const nav = this.navElement.nativeElement;
    this.navVerticalOverflow = nav.scrollHeight > nav.clientHeight;
  }

  private updateDrawerMode(width: number): void {
    this.drawerMode = width >= 1894 ? 'side' : 'over';
  }

  isDrawerModeSide(): boolean {
    return this.drawer?.mode === 'side';
  }

  isDrawerOpen(): boolean {
    return this.drawer?.opened;
  }

  logout() {
    this.authenticationService.logout();
  }

  loadNPS(nps: Nps) {
    this.vcNPS.loadWootric(nps);
  }

  startFadeInAnimation() {
    const content = document.querySelector('.content-container');
    if (content) {
      content.classList.remove('fade-in');
    }
  }

  endFadeInAnimation() {
    const content = document.querySelector('.content-container');
    if (content) {
      setTimeout(() => {
        content.classList.add('fade-in');
      }, 500);
    }
  }

  onScroll(event: Event) {
    const pageScrollElement = document.querySelector('.page-modules');
    const mainScrollElement = document.querySelector('#mainScrollElement');

    if (
      event.target !== pageScrollElement &&
      event.target !== mainScrollElement
    ) {
      return;
    }

    if ((<Element>event.target).scrollTop > 250) {
      this.showScrollToTopButton = true;
    } else {
      this.showScrollToTopButton = false;
    }
  }

  scrollToTop() {
    const pageScrollElement = document.querySelector('.page-modules');
    const mainScrollElement = document.querySelector('#mainScrollElement');

    if (mainScrollElement) {
      mainScrollElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (pageScrollElement) {
      pageScrollElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  routeTo(url: string) {
    if (!url.startsWith('https')) {
      this.routerService.openUrl([url]);
    } else {
      window.open(url, '_blank');
    }
  }
}
